import { gql, useQuery } from '@apollo/client';
import { Button } from '@apollo/orbit';
import { Moon } from '@apollo/space-kit/illustrations/Moon';
import { sortBy } from 'lodash';
import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { Loading } from 'src/components/common/loading/Loading';
import { URLModal } from 'src/components/common/URLModal';
import { useCurrentPlan } from 'src/hooks/currentPlanV2Migration';
import { useCurrentAccountId } from 'src/hooks/useCurrentAccountId';
import { useRouteParams } from 'src/hooks/useRouteParams';
import Config from 'src/lib/config';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { setOverlayVisibleLocation } from 'src/lib/routing';

import { variantRouteConfig } from '../../routes';

import { CreatePagerDutyChannelModal } from './addNotificationWizard/createPagerDutyChannel/CreatePagerDutyChannel';
import { CreateSlackChannelModal } from './addNotificationWizard/createSlackChannelModal/CreateSlackChannelModal';
import { Alerts } from './alertsIntegration/Alerts';
import {
  ChannelCard,
  ChannelCardChannelFragment,
} from './channelCard/ChannelCard';
import { DatadogIntegration } from './datadogIntegration/DatadogIntegration';
import {
  EditChannelModal,
  editChannelModalChannelFragment,
} from './editChannelModal/EditChannelModal';

const notificationsSettingsPageQuery = gql<
  GraphQLTypes.NotificationsSettingsPageQuery,
  GraphQLTypes.NotificationsSettingsPageQueryVariables
>`
  query NotificationsSettingsPageQuery($graphId: ID!) {
    service(id: $graphId) {
      id
      account {
        id
      }
      channels {
        id
        ...ChannelCardChannelFragment
        ...EditChannelModalChannelFragment
      }
    }
  }
  ${ChannelCardChannelFragment}
  ${editChannelModalChannelFragment}
`;

export function NotificationsSettingsPage() {
  const location = useLocation();
  const { graphId } = useRouteParams(variantRouteConfig);
  const [currentAccountId] = useCurrentAccountId();
  const currentPlan = useCurrentPlan({
    accountId: currentAccountId as string,
    skip: !currentAccountId,
  });

  const { data, loading, error } = useQuery(notificationsSettingsPageQuery, {
    variables: {
      graphId,
    },
  });
  const [editingChannelId, setEditingChannelId] = useState<string>();

  if (loading || currentPlan.loading) {
    return <Loading />;
  }

  if (!data?.service?.channels || error || currentPlan.error) {
    throw error || currentPlan.error || new Error('Something went wrong');
  }

  const { id: serviceId, channels } = data.service;
  const editingChannel =
    editingChannelId && channels.find((c) => c.id === editingChannelId);
  const datadogAvailable = !!currentPlan.currentPlan?.datadog;
  const notificationsAvailable = !!currentPlan.currentPlan?.notifications;

  return (
    <>
      {datadogAvailable ? <DatadogIntegration serviceId={serviceId} /> : null}
      {data.service?.account && notificationsAvailable ? (
        <Alerts
          serviceId={data.service.id}
          accountId={data.service.account.id}
        />
      ) : null}
      {channels.length === 0 ? (
        <div className="text-center">
          <Moon className="mb-6" />
          <div className="mb-6 text-3xl font-semibold">
            No notifications configured
          </div>
          <Button
            as={Link}
            to={setOverlayVisibleLocation({
              location,
              overlayName: Config.modals.addNotificationWizard,
            })}
          >
            Add a notification
          </Button>
        </div>
      ) : (
        <>
          {sortBy(channels, (channel) => channel.name).map((channel) => (
            <ChannelCard
              key={channel.id}
              graphId={serviceId}
              channel={channel}
              onEditChannelClick={(channelId: string) =>
                setEditingChannelId(channelId)
              }
            />
          ))}
        </>
      )}

      {editingChannel && (
        <EditChannelModal
          isOpen
          graphId={graphId}
          channel={editingChannel}
          onClose={() => setEditingChannelId(undefined)}
        />
      )}
      <URLModal name={Config.modals.createSlackChannel}>
        {({ isOpen, closeModal }) => (
          <CreateSlackChannelModal
            isOpen={isOpen}
            onChannelCreated={() => {
              closeModal();
            }}
            onClose={closeModal}
          />
        )}
      </URLModal>
      <URLModal name={Config.modals.createPagerDutyChannel}>
        {({ isOpen, closeModal }) => (
          <CreatePagerDutyChannelModal
            isOpen={isOpen}
            graphId={graphId}
            onClose={closeModal}
          />
        )}
      </URLModal>
    </>
  );
}
